import React, { useState, useEffect } from 'react';
import { GitBranch, Users, FileText, Clock, CheckCircle, AlertCircle, ArrowRight, Plus, Search } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';

const WorkflowManagement = () => {
  const [workflowStats, setWorkflowStats] = useState({
    pendingReview: 0,
    inReview: 0,
    readyToPublish: 0,
    published: 0
  });
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWorkflowData();
  }, []);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      // For now, we'll show placeholder data since the API might not be implemented yet
      setWorkflowStats({
        pendingReview: 45,
        inReview: 12,
        readyToPublish: 8,
        published: 234
      });

      setAssignments([
        {
          id: '1',
          type: 'Review Assignment',
          assignedTo: 'Content Reviewer 1',
          assignedBy: 'Content Supervisor',
          questionCount: 15,
          kitName: 'Mathematics Foundation',
          status: 'In Progress',
          dueDate: '2024-01-20T10:00:00Z',
          createdAt: '2024-01-15T09:00:00Z'
        },
        {
          id: '2',
          type: 'Generation Assignment',
          assignedTo: 'Content Generator 2',
          assignedBy: 'Content Supervisor',
          questionCount: 25,
          kitName: 'Physics Mechanics',
          status: 'Pending',
          dueDate: '2024-01-25T15:00:00Z',
          createdAt: '2024-01-16T11:30:00Z'
        }
      ]);
    } catch (error) {
      console.error('Error loading workflow data:', error);
      showErrorToast(error, 'Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = () => {
    showSuccessToast('Create assignment functionality coming soon!');
  };

  const handlePublishQuestions = () => {
    showSuccessToast('Publish questions functionality coming soon!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    return type.includes('Review') ? CheckCircle : FileText;
  };

  const filteredAssignments = assignments.filter(assignment =>
    assignment.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.kitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflow Management</h1>
          <p className="text-gray-600 mt-1">Manage content creation workflow and assignments</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handlePublishQuestions}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Publish Ready Questions
          </button>
          <button
            onClick={handleCreateAssignment}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Assignment
          </button>
        </div>
      </div>

      {/* Workflow Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{workflowStats.pendingReview}</div>
              <div className="text-sm text-gray-600">Pending Review</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <GitBranch className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{workflowStats.inReview}</div>
              <div className="text-sm text-gray-600">In Review</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{workflowStats.readyToPublish}</div>
              <div className="text-sm text-gray-600">Ready to Publish</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{workflowStats.published}</div>
              <div className="text-sm text-gray-600">Published</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search assignments by assignee, KIT, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="text-center py-12">
            <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No assignments found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'Create your first assignment to get started.'}
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateAssignment}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Assignment
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KIT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssignments.map((assignment) => {
                  const TypeIcon = getTypeIcon(assignment.type);
                  return (
                    <tr key={assignment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <TypeIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{assignment.type}</div>
                            <div className="text-sm text-gray-500">by {assignment.assignedBy}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{assignment.assignedTo}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{assignment.kitName}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{assignment.questionCount}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(assignment.status)}`}>
                          {assignment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {new Date(assignment.dueDate).toLocaleDateString()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowManagement;
